import {
  Alert,
  Button,
  Card,
  ColorInput,
  Group,
  Radio,
  Select,
  Slider,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useEffect, useState } from "react";
import { usePermission } from "@how/app-kit";
import {
  accentReadsAsText,
  PALETTE_DESCRIPTIONS,
  PALETTE_GROUNDS,
  PALETTE_LABELS,
  PALETTES,
  readableOn,
  TYPEFACE_DESCRIPTIONS,
  TYPEFACE_LABELS,
  TYPEFACES,
  type Brand,
  type Palette,
  type Typeface,
} from "@how/website-core";
import { LoadingState, PageHeader } from "@how/ui";
import { useTenantRecord } from "../tenant/queries";
import { useBrand, useSaveBrand } from "./brand-queries";
import { IntroControls, introDraftOf, introOfDraft, type IntroDraft } from "./IntroControls";
import { mediaUrl } from "./media-queries";
import { useAssets } from "./website-queries";
import { PalettePicker, SitePreview, TypefacePicker } from "./studio/LookStudio";
import { StudioShell } from "./studio/StudioShell";
import { useWebsiteVariant } from "./website-variant";
import { PALETTE_INK, TYPEFACE_STACKS } from "./look-values";
import { ThemeSection } from "./ThemeSection";
import { WebsiteTabs } from "./WebsiteTabs";

/**
 * A miniature of a church page: the ground, a headline, a line of text and the one accented action. It is drawn from
 * the same values the site is, so what a person sees here is what a visitor gets.
 */
function Preview({ brand }: { readonly brand: Brand }) {
  const ground = PALETTE_GROUNDS[brand.palette];
  const ink = PALETTE_INK[brand.palette];
  return (
    <div
      style={{
        background: ground,
        color: ink,
        borderRadius: "var(--mantine-radius-lg)",
        border: "1px solid var(--how-border-subtle)",
        padding: "28px 26px",
        fontFamily: TYPEFACE_STACKS[brand.typeface],
      }}
    >
      <div style={{ fontSize: 30, fontWeight: 650, letterSpacing: "-0.022em", lineHeight: 1.06 }}>
        You belong here
      </div>
      <div style={{ opacity: 0.72, marginTop: 8, fontSize: 14 }}>
        Every Sunday, and everyone welcome.
      </div>
      <div style={{ display: "flex", gap: 8, marginTop: 18, alignItems: "center" }}>
        <span
          style={{
            background: brand.accent,
            color: readableOn(brand.accent),
            borderRadius: 999,
            padding: "9px 18px",
            fontWeight: 600,
            fontSize: 13,
          }}
        >
          Plan a visit
        </span>
        <span
          style={{
            border: `2px solid ${ink}`,
            borderRadius: 999,
            padding: "7px 18px",
            fontWeight: 700,
            fontSize: 13,
          }}
        >
          About us
        </span>
      </div>
    </div>
  );
}

/**
 * Website → Look: the theme (ADR 0050), then the palette, the typeface, the accent and the logo, which carry into every
 * theme, and how the site opens (the intro, ADR 0073 amended 2026-10-14; only Revival plays it). The theme is applied
 * on its own through `set_site_theme`; the rest is saved with Save.
 */
export function LookPage() {
  const studio = useWebsiteVariant() === "studio";
  const stored = useBrand();
  const assets = useAssets();
  const tenant = useTenantRecord();
  const saveBrand = useSaveBrand();
  const canManage = usePermission("settings.manage", { write: true });
  const [brand, setBrand] = useState<Brand | null>(null);
  const [introDraft, setIntroDraft] = useState<IntroDraft | null>(null);

  useEffect(() => {
    if (stored.data !== undefined && brand === null) {
      setBrand(stored.data.brand);
      setIntroDraft(introDraftOf(stored.data.brand.intro));
    }
  }, [stored.data, brand]);

  if (stored.isPending || brand === null || introDraft === null) {
    if (studio) {
      return (
        <StudioShell section="look" title="Look">
          <LoadingState rows={3} />
        </StudioShell>
      );
    }
    return (
      <>
        <PageHeader title="Website" description="How your website looks to a visitor." />
        <WebsiteTabs active="look" />
        <LoadingState rows={3} />
      </>
    );
  }

  const editable = canManage.data === true;
  const accentIsReadable = accentReadsAsText(brand.accent, brand.palette);
  const logoOptions = (assets.data ?? []).map((a) => ({
    value: a.storage_path,
    label: a.alt_text,
  }));

  const introOutcome = introOfDraft(introDraft);
  const saveButton = editable ? (
    <Button
      loading={saveBrand.isPending}
      disabled={"problem" in introOutcome}
      onClick={() => {
        if ("problem" in introOutcome) return;
        saveBrand.mutate(
          { brand: { ...brand, intro: introOutcome.intro }, stored: stored.data?.stored ?? {} },
          {
            onSuccess: () => {
              notifications.show({
                color: "teal",
                title: "Saved",
                message: "Your website is using the new look.",
              });
            },
            onError: () => {
              notifications.show({
                color: "red",
                title: "Not saved",
                message: "Check your access and try again.",
              });
            },
          },
        );
      }}
    >
      Save
    </Button>
  ) : undefined;
  const accentControls = (
    <>
      <ColorInput
        label="Accent colour"
        aria-label="Accent colour"
        format="hex"
        disabled={!editable}
        value={brand.accent}
        onChange={(value) => {
          setBrand({ ...brand, accent: value });
        }}
        // Three that read on a light ground, three that read on a dark one.
        swatches={["#b04a42", "#1b4d3e", "#2f4f7f", "#e8a49c", "#f3d9a4", "#9ec7e8"]}
      />
      {accentIsReadable ? null : (
        <Alert variant="light" color="yellow" title="This colour cannot carry text">
          Your buttons will still use it, with a label chosen to read against it. Links will use
          your palette&apos;s text colour instead, because this accent measures below 4.5:1 on that
          ground and a visitor could not read it.
        </Alert>
      )}
    </>
  );
  const introControls = (
    <IntroControls
      draft={introDraft}
      siteName={tenant.data?.name ?? ""}
      disabled={!editable}
      onChange={setIntroDraft}
    />
  );
  const logoControls = (
    <>
      <Select
        label="Logo"
        aria-label="Logo"
        placeholder={logoOptions.length === 0 ? "No images uploaded yet" : "Use my church's name"}
        data={logoOptions}
        clearable
        searchable
        disabled={!editable || logoOptions.length === 0}
        value={brand.logoPath ?? null}
        onChange={(value) => {
          const next = { ...brand };
          if (value === null) delete (next as { logoPath?: string }).logoPath;
          else next.logoPath = value;
          setBrand(next);
        }}
      />
      {brand.logoPath === undefined ? null : (
        <>
          <Text size="sm" fw={600}>
            How wide it is drawn: {brand.logoWidth}px
          </Text>
          <Slider
            min={60}
            max={320}
            step={10}
            disabled={!editable}
            value={brand.logoWidth}
            onChange={(value) => {
              setBrand({ ...brand, logoWidth: value });
            }}
            aria-label="Logo width in pixels"
          />
          <img
            src={mediaUrl(brand.logoPath)}
            alt=""
            style={{ width: brand.logoWidth, maxWidth: "100%" }}
          />
        </>
      )}
    </>
  );

  if (studio) {
    return (
      <StudioShell
        section="look"
        title="Look"
        description="Your theme sets the layout, the type and the spacing. Within it, the palette, one accent colour, the typeface and your logo are yours, saved with Save."
        actions={saveButton}
      >
        <ThemeSection editable={editable} headingOrder={3} />
        <div className="ws-look">
          <div className="ws-look__controls">
            <section className="ws-panel" aria-labelledby="ws-palette-heading">
              <h3 className="ws-panel__title" id="ws-palette-heading">
                Palette
              </h3>
              <p className="ws-panel__text">
                The ground your pages are printed on. Visitors all see the one you choose; a church
                website does not follow a visitor&apos;s dark mode.
              </p>
              <PalettePicker
                brand={brand}
                disabled={!editable}
                onChange={(palette) => {
                  setBrand({ ...brand, palette });
                }}
              />
            </section>
            <section className="ws-panel" aria-labelledby="ws-typeface-heading">
              <h3 className="ws-panel__title" id="ws-typeface-heading">
                Typeface
              </h3>
              <p className="ws-panel__text">
                The face your words are set in, served from HoW so your pages never ask another
                company for a font on a visitor&apos;s behalf.
              </p>
              <TypefacePicker
                brand={brand}
                disabled={!editable}
                onChange={(typeface) => {
                  setBrand({ ...brand, typeface });
                }}
              />
            </section>
            <div className="ws-look__pair">
              <section className="ws-panel" aria-labelledby="ws-accent-heading">
                <h3 className="ws-panel__title" id="ws-accent-heading">
                  Accent
                </h3>
                <p className="ws-panel__text">
                  One colour, spent on one action per screen: the main button, and links.
                </p>
                <Stack gap="sm">{accentControls}</Stack>
              </section>
              <section className="ws-panel" aria-labelledby="ws-logo-heading">
                <h3 className="ws-panel__title" id="ws-logo-heading">
                  Logo
                </h3>
                <p className="ws-panel__text">
                  From your images. Without one, your church&apos;s name is set in type.
                </p>
                <Stack gap="sm">{logoControls}</Stack>
              </section>
            </div>
            <section className="ws-panel" aria-labelledby="ws-intro-heading">
              <h3 className="ws-panel__title" id="ws-intro-heading">
                How your site opens
              </h3>
              <p className="ws-panel__text">
                A short opening before the home page, in the Revival theme. Other themes go straight
                to the page.
              </p>
              {introControls}
            </section>
          </div>
          <aside className="ws-look__preview" aria-labelledby="ws-preview-heading">
            <div className="ws-panel">
              <h3 className="ws-panel__title" id="ws-preview-heading">
                What a visitor sees
              </h3>
              <p className="ws-panel__text">
                Your home page in the colours, face and logo above, as you change them.
              </p>
              <SitePreview brand={brand} />
              <p className="ws-note ws-look__saved">
                Saved changes appear on your website straight away. Pages themselves still need
                publishing.
              </p>
              {saveButton === undefined ? null : <div className="ws-look__save">{saveButton}</div>}
            </div>
          </aside>
        </div>
      </StudioShell>
    );
  }

  return (
    <>
      <PageHeader
        title="Website"
        description="How your website looks to a visitor."
        actions={saveButton}
      />
      <WebsiteTabs active="look" />

      <Stack gap="lg">
        <Alert variant="light" title="What you can change">
          Your theme sets the layout, the type and the spacing, and every theme keeps your pages
          readable and quick on a phone. Within it, what is yours is the palette, one accent colour,
          the typeface and your logo, saved with the Save button above. A theme is applied on its
          own, with Apply.
        </Alert>

        <ThemeSection editable={editable} />

        <Card>
          <Stack gap="md">
            <div>
              <Title order={2} size="h4">
                Palette
              </Title>
              <Text size="sm" c="dimmed">
                The ground your pages are printed on. Visitors all see the one you choose; a church
                website does not follow a visitor&apos;s dark mode.
              </Text>
            </div>
            <Radio.Group
              value={brand.palette}
              onChange={(value) => {
                setBrand({ ...brand, palette: value as Palette });
              }}
            >
              <Stack gap="xs">
                {PALETTES.map((palette) => (
                  <Radio
                    key={palette}
                    value={palette}
                    disabled={!editable}
                    label={PALETTE_LABELS[palette]}
                    description={PALETTE_DESCRIPTIONS[palette]}
                  />
                ))}
              </Stack>
            </Radio.Group>
          </Stack>
        </Card>

        <Card>
          <Stack gap="md">
            <div>
              <Title order={2} size="h4">
                Typeface
              </Title>
              <Text size="sm" c="dimmed">
                The face your words are set in. All three are served from HoW, so your pages never
                ask another company for a font on a visitor&apos;s behalf.
              </Text>
            </div>
            <Radio.Group
              value={brand.typeface}
              onChange={(value) => {
                setBrand({ ...brand, typeface: value as Typeface });
              }}
            >
              <Stack gap="xs">
                {TYPEFACES.map((face) => (
                  <Radio
                    key={face}
                    value={face}
                    disabled={!editable}
                    label={TYPEFACE_LABELS[face]}
                    description={TYPEFACE_DESCRIPTIONS[face]}
                  />
                ))}
              </Stack>
            </Radio.Group>
          </Stack>
        </Card>

        <Card>
          <Stack gap="md">
            <div>
              <Title order={2} size="h4">
                Accent
              </Title>
              <Text size="sm" c="dimmed">
                One colour, spent on one action per screen: the main button, and links. Anything
                more and a page has no accent at all.
              </Text>
            </div>
            {accentControls}
          </Stack>
        </Card>

        <Card>
          <Stack gap="md">
            <div>
              <Title order={2} size="h4">
                Logo
              </Title>
              <Text size="sm" c="dimmed">
                From your images. Without one, your church&apos;s name is set in type, which is a
                perfectly good header.
              </Text>
            </div>
            {logoControls}
          </Stack>
        </Card>

        <Card>
          <Stack gap="md">
            <div>
              <Title order={2} size="h4">
                How your site opens
              </Title>
              <Text size="sm" c="dimmed">
                A short opening before the home page, in the Revival theme. Other themes go straight
                to the page.
              </Text>
            </div>
            {introControls}
          </Stack>
        </Card>

        <Card>
          <Stack gap="md">
            <div>
              <Title order={2} size="h4">
                What a visitor sees
              </Title>
              <Text size="sm" c="dimmed">
                The same colours your pages are drawn with.
              </Text>
            </div>
            <Preview brand={brand} />
            <Group gap="xs">
              <Text size="xs" c="dimmed">
                Saved changes appear on your website straight away. Pages themselves still need
                publishing.
              </Text>
            </Group>
          </Stack>
        </Card>
      </Stack>
    </>
  );
}
